import * as React from 'react';
import { TranslationContext } from '@contexts/translation/TranslationContext';
import { WhitelabelAware } from '@whitelabel/components/WhitelabelAware/WhitelabelAware';
import { Experiment, OverrideExperiment } from '@contexts/experiment/types';
import { ExperimentContext } from '@contexts/experiment/ExperimentContext';
import { OverrideExperimentContext } from '@contexts/experiment/OverrideExperimentContext';
import { CmsTranslation } from '@contexts/translation/types';
import { useAppContext } from '@contexts/app/hooks/useAppContext';

export type DataFromCronos = {
    cms: CmsTranslation;
    experiments: Experiment;
    overrideExperiments: OverrideExperiment;
};

export type WithData<T> = {
    data: DataFromCronos;
} & T;

/**
 * provide data via Contexts
 * - translations
 * - experiments
 * - whitelabel aware
 *
 * @requires AppContext
 * @param Component
 */
export function withData<TProps>(Component: React.FC<TProps>): React.FC<WithData<TProps>> {
    const ComponentWithData: React.FC<WithData<TProps>> = props => {
        const { overrideExperiments, experiments, cms } = props.data;
        const { whitelabelId, cultureCode } = useAppContext();
        const memoized = React.useMemo(() => <Component {...props} />, [props]);

        return (
            <OverrideExperimentContext.Provider value={overrideExperiments}>
                <ExperimentContext.Provider value={experiments}>
                    <TranslationContext.Provider value={cms}>
                        <WhitelabelAware whitelabelId={whitelabelId} cultureCode={cultureCode}>
                            {memoized}
                        </WhitelabelAware>
                    </TranslationContext.Provider>
                </ExperimentContext.Provider>
            </OverrideExperimentContext.Provider>
        );
    };

    ComponentWithData.displayName = `${Component.displayName || 'Component'}WithData`;

    return ComponentWithData;
}
